import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import images from "../../../images";
import Button from "../../../components/common/Button/Button";
import Status from "../../../components/common/Status/Status";
import Requirement from "../../../components/common/Requirement/Requirement";
import Timer from "../DetailMultipleChoice/Timer";
import {
  cn,
  formatDateToFakeMicroUTC,
  getDayCountdown,
  getJoinQuestCountdown,
  getTimeRemaining,
  calculateTimeUntilStart,
  getCurrentUserId,
} from "../../../Utils/helper/index";
import StatusIcon from "../../../components/common/Status/StatusIcon/StatusIcon";
import NotifyMeButton from "../../../components/common/Button/NotifyMeButton";
// import { useAvailableCountdown } from "../../../hooks/useAvailableCountdown";

const TaskQuest = ({
  requirement,
  handleTaskQuest,
  token,
  estTime,
  expireIn,
  slotAvailable,
  consumerStatus,
  questType,
  isEnable,
  expireTime,
  numberHandleByQuota,
  quotaCount,
  quotaType,
  questData,
  isFavorited = false,
  handleFavoriteQuest,
  isLoadingFavorites,
  onClose,
  isLoggedIn,
  isQuestLoading,
}) => {
  const { t } = useTranslation();

  const [countdown, setCountdown] = useState(getJoinQuestCountdown());
  const [countdownDay, setCountdownDay] = useState(getDayCountdown());
  const [timeUntilStart, setTimeUntilStart] = useState(null);
  const [timeUntilCustomActive, setTimeUntilCustomActive] = useState(null);

  const startTime = questData?.quest_business?.start_time;
  // const {textContent, isFinished} = useAvailableCountdown("2026-01-27T01:54:06.070666", 5);

  // const { title } = questTypeMap[questType] || questTypeMap["PULSE_CHECK"];

  // Calculate Initial Remaining Time for Paused Quests
  const nextDayActive = questData?.next_day_active_quest;
  const calculateRemainingTime = () => {
    if (!expireIn) return 0;
    const now = new Date();
    const nowUTCISOString = formatDateToFakeMicroUTC(now);
    const expiredDate = new Date(expireIn.slice(0, 23));
    const nowDate = new Date(nowUTCISOString.slice(0, 23));
    const diffMs = expiredDate.getTime() - nowDate.getTime();
    return Math.round(diffMs / 1000);
  };

  const initialRemainingTime = useMemo(
    () => calculateRemainingTime(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expireIn],
  );
  const [remainingTime, setRemainingTime] = useState(
    initialRemainingTime > 0 ? initialRemainingTime : null,
  );

  // --- Effects ---

  // 1. Unified Interval for Countdowns and Start Time
  useEffect(() => {
    const updateTimers = () => {
      // Update Quota Countdowns
      setCountdown(getJoinQuestCountdown());
      setCountdownDay(getDayCountdown());

      // Update Start Time Countdown
      if (startTime) {
        const timeRemaining = calculateTimeUntilStart(startTime);
        setTimeUntilStart(timeRemaining.display);
      }
      if (
        quotaType === "CUSTOM" &&
        consumerStatus === "COMPLETED" &&
        nextDayActive
      ) {
        const customRemaining = calculateTimeUntilStart(nextDayActive);
        setTimeUntilCustomActive(customRemaining.display);
      } else {
        setTimeUntilCustomActive(null);
      }
    };

    updateTimers(); // Run once immediately
    const interval = setInterval(updateTimers, 1000);
    return () => clearInterval(interval);
  }, [startTime, quotaType, consumerStatus, nextDayActive]);

  // 2. Timer for Paused Quest Countdown
  useEffect(() => {
    if (initialRemainingTime <= 0 || consumerStatus !== "PAUSED_QUEST") {
      setRemainingTime(null);
      return;
    }

    const timer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime === null || prevTime <= 1) {
          clearInterval(timer);
          return null;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [initialRemainingTime, consumerStatus]);

  // --- Helpers & Configuration ---

  const isQuestExpired = questData?.quest_business?.status_biz === "EXPIRED";
  const isQuestCompleted =
    questData?.quest_business?.status_biz === "COMPLETED" ||
    consumerStatus === "COMPLETED";
  const isQuestDoing =
    consumerStatus === "DOING" || consumerStatus === "PAUSED_QUEST";
  const isWaitingToStart = !!timeUntilStart;
  const isReceiptRequired = questData?.is_receipt_required;
  const isContentActiveForStyling = isEnable;
  // &&
  // questData?.quest_business?.status_biz !== "COMPLETED";
  const isQuestFull = slotAvailable <= 0;
  const isWaitingForNextActive = !!timeUntilCustomActive;

  const textColor = isContentActiveForStyling
    ? "text-[#253FF6] "
    : "text-[#A0A0A0]";
  const backgroundColor = isContentActiveForStyling
    ? "bg-[#F6F7FF]"
    : "bg-[#F4F2F2]";

  const timeInfo = useMemo(
    () => getTimeRemaining(expireTime, t),
    [expireTime, t],
  );

  const TIME_STATUS_EXCEPTIONS = ["Expired", "Invalid date", "Expiring soon"];
  const shouldShowSuffix = !TIME_STATUS_EXCEPTIONS.includes(timeInfo.display);

  const getButtonConfig = () => {
    if (slotAvailable <= 0 && consumerStatus === "NOT_STARTED") {
      return {
        text: t("quest.questDetails.noSlotAvailable"),
        color: "bg-gray-300",
        textColor: "text-gray-500",
        disabled: true,
      };
    }

    if (questType === "REFERRAL" && consumerStatus !== "COMPLETED") {
      return {
        text: t("quest.taskQuest.takeQuest"),
        color: "bg-[#253FF6]",
        textColor: "text-white",
        disabled: false,
      };
    }
    if (
      isQuestExpired &&
      (consumerStatus === "NOT_STARTED" || consumerStatus === "SAVE_QUEST")
    ) {
      return {
        text: t("timeRemaining.expired"),
        color: "bg-[#E9E6E4]",
        textColor: "text-[#A0A0A0]",
        disabled: true,
      };
    }
    if (slotAvailable <= 0 && consumerStatus === "REJECT") {
      return {
        text: t("base.full"),
        color: "bg-gray-300",
        textColor: "text-gray-500",
        disabled: true,
      };
    }
    if (questData?.quest_business?.status_biz === "COMPLETED") {
      return {
        text: t("quest.taskQuest.Ineligible"),
        color: "bg-[#E9E6E4]",
        textColor: "text-[#A0A0A0]",
        disabled: true,
      };
    }

    switch (consumerStatus) {
      case "NOT_STARTED":
        return {
          text: t("quest.taskQuest.takeQuest"),
          color: "bg-[#253FF6]",
          textColor: "text-white",
          disabled: false,
        };
      case "PAUSED_QUEST":
        return {
          text: t("quest.taskQuest.continue"),
          color: "bg-[#EF4056]",
          textColor: "text-white",
          disabled: false,
        };
      case "DOING":
        return {
          text: t("quest.taskQuest.continue"),
          color: "bg-[#EF4056]",
          textColor: "text-white",
          disabled: false,
        };
      case "SAVE_QUEST":
        return {
          text: t("quest.taskQuest.reJoinQuest"),
          color: "bg-[#00B88C]",
          textColor: "text-white",
          disabled: false,
        };
      case "FULL":
        return {
          text: t("quest.taskQuest.full"),
          color: "bg-[#00B88C]",
          textColor: "text-white",
          disabled: true,
        };
      case "INELIGIBLE":
        return {
          text:
            consumerStatus === "FULL"
              ? t("quest.taskQuest.full")
              : t("quest.taskQuest.Ineligible"),
          color: "bg-[#E9E6E4]",
          textColor: "text-[#A0A0A0]",
          disabled: true,
        };
      case "COMPLETED":
        return {
          text:
            consumerStatus === "COMPLETED"
              ? t("base.completed")
              : t("quest.taskQuest.eligible"),
          color: "bg-gray-300",
          textColor: "text-gray-500",
          disabled: true,
        };
      case "REVIEWING":
        return {
          text:
            consumerStatus === "COMPLETED"
              ? t("base.completed")
              : t("quest.taskQuest.reviewing"),
          color: "bg-gray-300",
          textColor: "text-gray-500",
          disabled: true,
        };
      default:
        return {
          text: t("quest.taskQuest.takeQuest"),
          color: "bg-[#253FF6]",
          textColor: "text-white",
          disabled: false,
        };
    }
  };

  const getStatusQuestConfig = () => {
    if (
      isQuestFull &&
      (consumerStatus === "NOT_STARTED" || consumerStatus === "REJECT")
    ) {
      return "full";
    }
    if (questType === "REFERRAL" && consumerStatus !== "COMPLETED") {
      return "eligible";
    }
    if (isQuestExpired) {
      return "expired";
    }

    if (isQuestCompleted) {
      return "completed";
    }
    if (isQuestDoing) {
      return "paused";
    }
    if (!isContentActiveForStyling) {
      return "ineligible";
    }
    if (
      numberHandleByQuota === 0 &&
      (quotaType === "DAILY" || quotaType === "WEEKLY")
    ) {
      return "eligible";
    }
    if (quotaType === "DAILY" && questData?.participants?.length === 2) {
      return getQuestStatus(questData);
    }
    if (
      numberHandleByQuota === 0 &&
      (quotaType === "DAILY" || quotaType === "WEEKLY")
    ) {
      return "eligible";
    }
    if (isWaitingToStart) {
      return "unavailable";
    }
    switch (consumerStatus) {
      case "PAUSED_QUEST":
        return "paused";
      case "DOING":
        return "paused";
      case "SAVE_QUEST":
        return "saved";
      case "FULL":
        return "full";
      case "REVIEWING":
        return "reviewing";
      default:
        return "eligible";
    }
  };

  const {
    text,
    color: btnColor,
    textColor: btnTextColor,
    disabled,
  } = getButtonConfig();

  const getQuestStatus = (data) => {
    const currentUserId = getCurrentUserId();

    const allParticipants = data?.participants || [];

    const myParticipants = allParticipants.filter(
      (p) => String(p.consumer_id) === String(currentUserId),
    );

    if (myParticipants.length < 2)
      return myParticipants[myParticipants.length - 1]?.status;

    const [s1, s2] = myParticipants.map((p) => p.status);

    if (
      (s1 === "COMPLETED" && s2 === "COMPLETED") ||
      data?.quest_business?.status_biz === "COMPLETED"
    ) {
      return "COMPLETED";
    }

    return s1 === "COMPLETED" ? s2 : s1;
  };
  // 1. Định nghĩa logic xác định trạng thái hiển thị
  const renderActionButton = () => {
    if (isQuestLoading) {
      return <div className="h-[40px] xs:h-[56px]" />;
    }

    if (isWaitingForNextActive && consumerStatus === "COMPLETED") {
      return (
        <NotifyMeButton questData={questData} customStartDate={nextDayActive} />
      );
    }

    if (isWaitingToStart && isEnable) {
      return <NotifyMeButton questData={questData} t={t} />;
    }

    if (!isLoggedIn) {
      return (
        <Button
          text={t("base.signUpLogIn") || "Sign Up/Log In"}
          onClick={handleTaskQuest}
          className="w-full font-medium py-2.5 xs:py-4 text-white bg-[#253FF6] rounded-md text-base xs:text-xl mt-0"
        />
      );
    }

    if (consumerStatus === "COMPLETED") {
      return (
        <div className="w-full py-2.5 xs:py-4 text-[#A0A0A0] bg-[#E9E6E4] text-center font-medium text-base xs:text-xl pointer-events-none select-none mt-0 rounded-md">
          {t("base.completed")}
        </div>
      );
    }

    if (!isContentActiveForStyling) {
      return (
        <div className="w-full py-2.5 xs:py-4 bg-[#E9E6E4] text-[#A0A0A0] text-center font-medium text-base xs:text-xl pointer-events-none select-none mt-0 rounded-md">
          {t(isQuestExpired ? "timeRemaining.expired" : "base.lock")}
        </div>
      );
    }

    // Trạng thái mặc định (Action Button)
    return (
      <Button
        text={text}
        onClick={handleTaskQuest}
        className={cn(
          "w-full font-medium py-2.5 xs:py-4 rounded-md mt-0 text-base xs:text-xl gap-0",
          btnColor,
          btnTextColor,
          disabled &&
            "pointer-events-none bg-[#E9E6E4] text-[#A0A0A0] font-medium mt-0",
        )}
        disabled={disabled}
      />
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col justify-center w-full relative bg-transparent gap-3 sm:gap-6",
      )}
    >
      <div className="flex flex-col gap-3 sm:gap-6 w-full">
        {/* Header Section */}
        <div className="flex justify-between gap-2 xs:gap-3 w-full">
          <div className="flex flex-col gap-3 xs:gap-6">
            <div className="flex flex-col gap-1 xs:gap-3 justify-start items-start">
              <p
                className={cn(
                  "text-lg xs:text-2xl font-bold text-[#253FF6] text-left",
                  textColor,
                )}
              >
                {questData?.quest_business?.name || ""}
                {/* {title} */}
              </p>
              <div className="text-left">
                <p
                  className={cn(
                    "font-figtree font-medium text-xs xs:text-sm text-[#253FF6] opacity-50",
                    textColor,
                  )}
                >
                  {questData?.quest_business?.description || ""}
                </p>
              </div>

              <Status status={getStatusQuestConfig()} />
            </div>
          </div>
          <div>
            <button
              onClick={onClose}
              className="w-[24px] h-[24px] xs:w-[30px] xs:h-[30px] flex items-center justify-center hover:bg-gray-100 transition-colors duration-200 z-10"
              aria-label="Close modal"
            >
              <img
                src={images.CloseModalIconGray}
                className="w-4 h-4 xs:w-5 xs:h-5"
                alt="Close"
              />
            </button>
          </div>
        </div>

        {/* Info Blocks (Rewards & Tasks) */}
        <div className="flex w-full items-stretch justify-start gap-2 xs:gap-[18px] self-stretch border-[#E9E6E4]">
          {/* Rewards Block */}
          <div
            className={cn(
              "flex flex-col gap-1 xs:gap-3 flex-1 p-2 xs:p-3 xs:pb-[18px] rounded-md bg-[#F6F7FF] items-start justify-start",
              backgroundColor,
            )}
          >
            <span
              className={cn(
                "text-[#253FF6] text-[10px] xs:text-xs text-left font-medium opacity-50",
                textColor,
              )}
            >
              {t("profile.rewards")}
            </span>
            {token > 0 && (
              <div
                className={cn(
                  "flex items-center gap-1 xs:gap-2 text-[#253FF6] font-bold text-[11px] xs:text-sm",
                  textColor,
                )}
              >
                <span className="icon icon-rally-coin text-xl" />
                <span className="truncate">
                  {token} {token <= 1 ? t("base.coin") : t("base.coins")}
                </span>
              </div>
            )}

            {questData?.reward_stickers?.length > 0 && (
              <div
                className={cn(
                  "flex items-center gap-1 xs:gap-2 text-[#253FF6] font-bold text-[11px] xs:text-sm",
                  textColor,
                )}
              >
                <span className="icon icon-sticker text-xl" />

                <span className="truncate">
                  {questData?.reward_stickers?.length ?? 0}{" "}
                  {(questData?.reward_stickers?.length ?? 0) <= 1
                    ? t("quest.taskQuest.sticker")
                    : t("quest.taskQuest.stickers")}
                </span>
              </div>
            )}
            {questData?.quest_business?.xp > 0 && (
              <div
                className={cn(
                  "flex items-center gap-1 xs:gap-2 text-[#253FF6] font-bold text-[11px] xs:text-sm",
                  textColor,
                )}
              >
                <span className="icon icon-xp text-xl" />

                <span>{questData?.quest_business?.xp || 0} XP</span>
              </div>
            )}
          </div>

          {/* Task Info Block */}
          <div
            className={cn(
              "flex flex-col gap-1 xs:gap-3 flex-1 p-2 xs:p-3 xs:pb-[18px] rounded-md bg-[#F6F7FF] items-start justify-start",
              backgroundColor,
            )}
          >
            <span
              className={cn(
                "text-[#253FF6] text-[10px] xs:text-xs text-left font-medium opacity-50",
                textColor,
              )}
            >
              {t("quest.taskQuest.task")}
            </span>

            {isReceiptRequired && (
              <div
                className={cn(
                  "flex items-center justify-start gap-1 xs:gap-1.5 text-[#253FF6] font-bold text-[11px] xs:text-sm",
                  textColor,
                )}
              >
                {/* <div className="h-7 flex items-center justify-center">
                  <StatusIcon
                    icon="ticket"
                    className={!isContentActiveForStyling && "bg-[#A0A0A0]"}
                  />
                </div> */}
                <div className="h-7 flex items-center justify-center">
                  <img
                    src={images.ReceiptRequiredIcon}
                    className={cn(
                      "w-5 h-5 object-contain",
                      !isReceiptRequired && "grayscale opacity-60",
                    )}
                    alt="receipt"
                  />
                </div>
                <span
                  className={cn(
                    "whitespace-normal",
                    isContentActiveForStyling
                      ? "text-[#00B88C]"
                      : "text-[#A0A0A0]",
                  )}
                >
                  {t("quest.taskQuest.receiptRequired")}
                </span>
              </div>
            )}

            <div
              className={cn(
                "flex  items-center gap-1 xs:gap-2 text-[#253FF6] font-bold text-[11px] xs:text-sm",
                textColor,
              )}
            >
              {/* <img
                src={images.CircleHelpActiveIconBlue}
                className="w-3.5 h-3.5 xs:w-5 xs:h-5 object-contain"
                alt="questions"
              /> */}
              <div className="h-7 flex items-center justify-center">
                <StatusIcon
                  icon="help"
                  classNameIcon="text-[10px]"
                  className={cn(
                    "bg-[#A0A0A0]",
                    isContentActiveForStyling && "bg-[#253FF6]",
                  )}
                />
              </div>

              <span className={cn("truncate")}>
                {questData?.total_questions || "10"} {t("base.questions")}
              </span>
            </div>
            <div
              className={cn(
                "flex items-center gap-1 xs:gap-2 text-[#253FF6] font-bold text-[11px] xs:text-sm",
                textColor,
              )}
            >
              <div className="h-7 flex items-center justify-center">
                <StatusIcon
                  icon="clock"
                  classNameIcon="text-[10px]"
                  className={cn(
                    "bg-[#A0A0A0]",
                    isContentActiveForStyling && "bg-[#253FF6]",
                  )}
                />
              </div>

              <span className={cn("font-bold truncate")}>
                {t("quest.taskQuest.estTimeMins", { count: estTime })}
              </span>
            </div>
          </div>
        </div>

        {/* Requirements */}
        <div className="border-[#E9E6E4] ">
          {requirement && (
            <div className="flex flex-col items-start gap-1 xs:gap-3 self-stretch justify-start">
              <h6
                className={cn(
                  "font-[Figtree] text-sm xs:text-[18px] font-bold leading-tight xs:leading-[28px] text-left text-[#333]",
                  !isContentActiveForStyling && "text-[#EF4056]",
                )}
              >
                {t("quest.taskQuest.instructions")}:
              </h6>
              <div
                className={cn(
                  "font-[Figtree] text-xs xs:text-[16px] font-medium leading-normal xs:leading-[24px] w-full text-[#333]",
                  !isContentActiveForStyling && "text-[#EF4056]",
                )}
              >
                <Requirement
                  body={requirement}
                  isContentActiveForStyling={isContentActiveForStyling}
                  isEnable={isEnable}
                  className="mt-0 border-none p-0 text-left"
                />
              </div>
            </div>
          )}
        </div>

        {/* Availability Details */}
        {/* <div className="flex flex-col items-start gap-1 xs:gap-3 w-full">
          <div className="flex items-center gap-1 xs:gap-[6px]">
            <span className="icon icon-calendar text-sm xs:text-lg text-[#A0A0A0]" />
            <span className="text-[#A0A0A0] font-[Figtree] text-xs xs:text-base font-medium">
              {questType === "SNAP" ? (
                t("quest.taskQuest.availableTwiceDaily")
              ) : (
                <>
                  <>
                    {timeInfo.display}
                    {shouldShowSuffix && ` ${t("quest.taskQuest.left")}`}
                  </>
                </>
              )}
            </span>
          </div>
          <div className="flex items-center gap-1 xs:gap-[6px]">
            <span className="icon icon-user text-sm xs:text-lg text-[#A0A0A0]" />

            <span className="text-[#A0A0A0] font-[Figtree] text-xs xs:text-base font-medium">
              {t("quest.taskQuest.slotsLeft", {
                count: Math.max(0, slotAvailable),
              })}
            </span>
          </div>
        </div> */}

        {/* Waiting to Start Banner */}
        {isWaitingToStart && !isWaitingForNextActive && (
          <div className="flex justify-center items-center w-full py-2 xs:py-3 rounded bg-[#E9E6E4]">
            <div className="flex gap-2 items-center">
              <img
                src={images.ClockDisabled}
                className="w-4 h-4 xs:w-6 xs:h-6"
                alt="clock"
              />
              <span className="text-[#A0A0A0] font-medium text-sm xs:text-xl">
                Come back in {timeUntilStart}
              </span>
            </div>
          </div>
        )}

        {isWaitingForNextActive && consumerStatus === "COMPLETED" && (
          <div className="flex justify-center items-center w-full py-2 xs:py-3 rounded bg-[#E9E6E4]">
            <div className="flex gap-2 items-center">
              <img
                src={images.ClockDisabled}
                className="w-4 h-4 xs:w-6 xs:h-6"
                alt="clock"
              />
              <span className="text-[#A0A0A0] font-medium text-sm xs:text-xl">
                Come back in {timeUntilCustomActive}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="mx-auto flex flex-col gap-3 sm:gap-6 w-full bg-white">
        {consumerStatus === "PAUSED_QUEST" &&
          remainingTime !== null &&
          remainingTime > 0 && (
            <div className="mb-2 px-2 xs:px-[32px]">
              <Timer
                time={remainingTime}
                isStatic={true}
                className="rounded-md"
              />
            </div>
          )}

        {(quotaType === "DAILY" || quotaType === "WEEKLY") &&
          numberHandleByQuota === quotaCount && (
            <div className="w-full  ">
              <div className="w-full flex items-center justify-center gap-1.5 text-center py-3 px-[18px] rounded-md pointer-events-none bg-[#E9E6E4] text-[#A0A0A0] text-base xs:text-xl font-medium">
                <span className="icon icon-clock" />
                {quotaType === "WEEKLY" ? countdown : countdownDay}
              </div>
            </div>
          )}

        {renderActionButton()}

        {/* Quota Countdowns */}
      </div>
      {(questData?.quest_business?.show_time_left ||
        questData?.quest_business?.show_slot_left) && (
        <div className="inline-grid gap-x-[75px] gap-y-[75px] self-stretch grid-cols-2">
          {questData?.quest_business?.show_time_left && (
            <div className="flex items-center gap-[6px] col-start-1 col-span-1 row-start-1 row-span-1 justify-self-stretch">
              <span className="icon icon-calendar text-sm xs:text-lg text-[#A0A0A0]" />
              <span className="text-[#A0A0A0] font-[Figtree] text-xs xs:text-base font-medium">
                {questType === "SNAP" ? (
                  t("quest.taskQuest.availableTwiceDaily")
                ) : (
                  <span>
                    <>
                      {timeInfo.display}
                      {shouldShowSuffix && ` ${t("quest.taskQuest.left")}`}
                    </>
                  </span>
                )}
              </span>
            </div>
          )}
          {questData?.quest_business?.show_slot_left && (
            <div className="flex items-center gap-[6px] col-start-2 col-span-1 row-start-1 row-span-1 justify-self-stretch">
              <span className="icon icon-user text-sm xs:text-lg text-[#A0A0A0]" />

              <span className="text-[#A0A0A0] font-[Figtree] text-xs xs:text-base font-medium">
                {t("quest.taskQuest.slotsLeft", {
                  count: Math.max(0, slotAvailable),
                })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskQuest;
